import type { UserRole } from '../features/auth';
import type { AdminSection } from '../pages/admin/types';
import type { ResourceLocation } from '../shared/navigation/resource-location';
type DashboardKind = 'projects' | 'assets' | 'work' | 'plans' | 'inspections';
export type AppRoute =
  | { area: 'dashboard'; kind?: DashboardKind; id?: string }
  | {
      area: 'map';
      modelId?: string;
      projectId?: string;
      projectTab?: 'overview' | 'models' | 'assets' | 'documents';
    }
  | { area: 'admin'; section: AdminSection; resource?: ResourceLocation }
  | { area: 'document-preview'; projectId: string; documentId: string; revisionId?: string }
  | { area: 'ifc-viewer'; modelId: string }
  | { area: 'login' }
  | { area: 'not-found' };
export function parseAppRoute(pathname: string): AppRoute {
  const path = pathname.replace(/\/+$/, '') || '/';
  const dashboard = path.match(
    /^\/view\/dashboard(?:\/(projects|assets|work|plans|inspections)(?:\/([^/]+))?)?$/,
  );
  if (dashboard) {
    const id = dashboard[2] ? decodeResourceId(dashboard[2]) : undefined;
    if (dashboard[2] && !id) return { area: 'not-found' };
    return { area: 'dashboard', kind: dashboard[1] as DashboardKind | undefined, id };
  }
  if (path === '/login') return { area: 'login' };
  if (path === '/' || path === '/view') return { area: 'map' };
  const revisionPreview = path.match(/^\/view\/documents\/([^/]+)\/revisions\/([^/]+)\/preview$/);
  if (revisionPreview) {
    const documentId = decodeResourceId(revisionPreview[1]);
    const revisionId = decodeResourceId(revisionPreview[2]);
    return documentId && revisionId
      ? { area: 'document-preview', projectId: '', documentId, revisionId }
      : { area: 'not-found' };
  }
  const documentPreview = path.match(/^\/view\/projects\/([^/]+)\/documents\/([^/]+)\/preview$/);
  if (documentPreview) {
    const projectId = decodeResourceId(documentPreview[1]);
    const documentId = decodeResourceId(documentPreview[2]);
    return projectId && documentId
      ? { area: 'document-preview', projectId, documentId }
      : { area: 'not-found' };
  }
  const project = path.match(/^\/view\/projects\/([^/]+)(?:\/(models|assets|documents))?$/);
  if (project) {
    const projectId = decodeResourceId(project[1]);
    return projectId
      ? {
          area: 'map',
          projectId,
          projectTab: (project[2] || 'overview') as 'overview' | 'models' | 'assets' | 'documents',
        }
      : { area: 'not-found' };
  }
  const ifcViewer = path.match(/^\/view\/models\/([^/]+)\/ifc$/);
  if (ifcViewer) {
    const modelId = decodeResourceId(ifcViewer[1]);
    return modelId ? { area: 'ifc-viewer', modelId } : { area: 'not-found' };
  }
  const viewer = path.match(/^\/view\/models\/([^/]+)$/);
  if (viewer) {
    const modelId = decodeResourceId(viewer[1]);
    return modelId ? { area: 'map', modelId } : { area: 'not-found' };
  }
  if (path === '/admin' || path === '/admin/overview')
    return { area: 'admin', section: 'overview' };
  if (path === '/admin/users') return { area: 'admin', section: 'users' };
  if (path === '/admin/backups') return { area: 'admin', section: 'backups' };
  if (path === '/admin/access') return { area: 'admin', section: 'access' };
  if (path === '/admin/imports') return { area: 'admin', section: 'imports' };
  if (path === '/admin/models') return { area: 'admin', section: 'models' };
  if (path === '/admin/gis') return { area: 'admin', section: 'gis' };
  if (path === '/admin/layers') return { area: 'admin', section: 'layers' };
  if (path === '/admin/projects') return { area: 'admin', section: 'projects' };
  if (path === '/admin/maintenance') return { area: 'admin', section: 'maintenance' };
  if (path === '/admin/audit-logs') return { area: 'admin', section: 'audit-logs' };
  if (path === '/admin/system-logs') return { area: 'admin', section: 'system-logs' };
  const resourceMatch = path.match(/^\/admin\/(projects|imports|models|gis)\/([^/]+)(\/edit)?$/);
  if (resourceMatch) {
    const [, section, encodedId, edit] = resourceMatch;
    if (encodedId === 'new')
      return edit
        ? { area: 'not-found' }
        : { area: 'admin', section: section as AdminSection, resource: { mode: 'create' } };
    const resourceId = decodeResourceId(encodedId);
    if (!resourceId) return { area: 'not-found' };
    if (edit && section === 'gis') return { area: 'not-found' };
    return {
      area: 'admin',
      section: section as AdminSection,
      resource: { mode: edit ? 'edit' : 'detail', resourceId },
    };
  }
  return { area: 'not-found' };
}
function decodeResourceId(value: string): string | undefined {
  try {
    const id = decodeURIComponent(value);
    return id && !/[\/\\\u0000-\u001f]/.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
}
export function adminResourcePath(section: AdminSection, location: ResourceLocation): string {
  const base = `/admin/${section}`;
  if (location.mode === 'list') return base;
  if (location.mode === 'create') return `${base}/new`;
  if (!location.resourceId) return base;
  return `${base}/${encodeURIComponent(location.resourceId)}${location.mode === 'edit' ? '/edit' : ''}`;
}
export function canAccessRoute(route: AppRoute, role: UserRole | undefined): boolean {
  return (
    route.area !== 'admin' ||
    (!!role &&
      (['overview', 'projects', 'models', 'gis', 'layers', 'maintenance'].includes(route.section) ||
        role === 'admin'))
  );
}
